import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { LatLng } from 'leaflet';
import ObjectId from 'bson-objectid';

// Hooks
import { useAppDispatch, useAppSelector } from '../redux/hook';

// Actions
import { fetchReaderByImei, fetchNearReader, fetchReaderById } from '../redux/slices/readerSlice';
import { fetchAllSettings } from '../redux/slices/settingsSlice';
import { fetchReaderReportByImei } from '../redux/slices/readerReportSlice';
import { fetchStationGeofences } from '../redux/slices/geofenceSlice';

// Types
import { FilterableTypes } from '../types/FilterableTypes';
import { Coordinates } from '../types/Coordinates';
import { Facility } from '../types/Facility';

// Constants
import { getCoordinatesPreset } from '../constants/Constants';

// Utils
import { isValidObjectId } from '../helpers/stringhelper';
import useDateTimeFormat from './useDateTimeFormat';
import { isEqual } from 'lodash';

const useReaderDetail = () => {
  const dispatch = useAppDispatch();
  const { identifier } = useParams();

  const { toStandardDate } = useDateTimeFormat();

  // State Variables
  const [centerMap, setCenterMap] = useState<LatLng>();
  const [positions, setPositions] = useState<Coordinates[]>([]);
  const [shouldFetch, setShouldFetch] = useState(false);

  // Redux State
  const readerState = useAppSelector((state) => state.readers);
  const readerReportState = useAppSelector((state) => state.readerReports);
  const facilitiesState = useAppSelector((state) => state.facilities);
  const geofenceState = useAppSelector((state) => state.geofences);
  const settingsState = useAppSelector((state) => state.settings);

  const fetchReaderData = useCallback(async (id: string) => {
    if (isValidObjectId(id)) {
      await Promise.all([dispatch(fetchReaderById(id)), dispatch(fetchAllSettings())]);
    } else {
      await Promise.all([dispatch(fetchReaderByImei(id)), dispatch(fetchAllSettings())]);
    }
  }, []);

  useEffect(() => {
    setCenterMap(undefined);
    setPositions([]);

    if (identifier) {
      fetchReaderData(identifier).then(() => {
        setShouldFetch(true);
      });
    }
  }, [identifier, fetchReaderData]);

  const handleConfigUpdated = useCallback((identifier: string) => dispatch(fetchReaderByImei(identifier)), []);

  const fetchingData = useCallback(() => {
    const centroid = readerState.reader?.centroid;
    const lockedLocation = readerState.reader?.lockedLocation;
    const location = centroid || lockedLocation;
    if (readerState && location) {
      dispatch(
        fetchNearReader({
          lat: location?.latitude ?? 0,
          lng: location?.longitude ?? 0,
          distanceInMeters: 10000,
          filters: [],
        }),
      );
    }
    dispatch(
      fetchReaderReportByImei({
        filters: [{ imei: identifier, filterType: FilterableTypes.Equals }],
        sortBy: [{ lastSeenDateTime: '-1' }],
      }),
    );
    dispatch(
      fetchStationGeofences({
        filters: [
          { level: 0, filterType: FilterableTypes.Equals },
          { stationId: readerState.reader?.stationId, filterType: FilterableTypes.Equals },
        ],
        skip: 0,
        take: 50,
      }),
    );
  }, [readerState]);

  const initialCenterMap = (currentCenterMap: LatLng): Coordinates[] => {
    return [
      {
        ...getCoordinatesPreset('CentroidCircle'),
        latLng: new LatLng(currentCenterMap.lat, currentCenterMap.lng),
        showMarker: false,
        showCircleMarker: true,
        circleRadius: 300,
      },
      {
        ...getCoordinatesPreset('CentroidCircle'),
        latLng: new LatLng(currentCenterMap.lat, currentCenterMap.lng),
        fillOpacity: 1.0,
        showMarker: false,
        showCircleMarker: true,
        circleRadius: 5,
      },
    ];
  };

  useEffect(() => {
    if (readerState?.reader) {
      const { location, lockedLocation } = readerState.reader;
      const currentCenterMap =
        lockedLocation && readerState.isCalibration
          ? new LatLng(lockedLocation?.latitude, lockedLocation?.longitude)
          : new LatLng(location?.latitude ?? 0, location?.longitude ?? 0);

      if (!centerMap || !currentCenterMap.equals(centerMap)) {
        let newPositions: Coordinates[] = [];

        // Add a circle around the reader's location
        if (currentCenterMap) {
          newPositions = initialCenterMap(currentCenterMap);
        }

        // Add a circle around the reader's centroid
        // if (readerState.reader?.centroid) {
        //   const readerCentroid = new LatLng(readerState.reader.centroid.latitude, readerState.reader.centroid.longitude);
        //   const { stdDevCentroid } = readerState.reader;

        //   newPositions.push({
        //     ...getCoordinatesPreset('Centroid'),
        //     latLng: readerCentroid,
        //     markerName: MarkerReader.markerName,
        //     isReaderCentroid: true,
        //     showMarker: true,
        //     showCircleMarker: true,
        //     circleRadius: stdDevCentroid ? stdDevCentroid * 1000 : 10,
        //   });
        // }

        setPositions(newPositions);
        setCenterMap(currentCenterMap);
      }
      fetchingData();
    }
  }, [readerState?.reader]);

  useEffect(() => {
    if (readerState.reader && !readerState.isEditLockedLocation) {
      const { location, lockedLocation } = readerState.reader;
      const currentCenterMap =
        lockedLocation && readerState.isCalibration
          ? new LatLng(lockedLocation?.latitude, lockedLocation?.longitude)
          : new LatLng(location?.latitude ?? 0, location?.longitude ?? 0);

      // update center when lockedLocation has value set
      setPositions((prevPositions) => {
        let currentPositions = [...prevPositions];
        currentPositions = currentPositions.filter((v) => v._type !== 'CentroidCircle');
        currentPositions = [...currentPositions, ...initialCenterMap(currentCenterMap)];
        return currentPositions;
      });

      setCenterMap(currentCenterMap);
    }
  }, [readerState.isCalibration, readerState.isEditLockedLocation]);

  // NearReader
  const nearReadersPositions = useMemo<Coordinates[]>((): Coordinates[] => {
    if (!readerState.nearReaders) return [];

    // Only show fixed && locked readers
    // Filtering logic for NearReader toggles
    const filtered = readerState.nearReaders.filter((x) => x.lockedLocation);
    // Add opsStatus filtering based on UI toggle (pseudo-code, replace with actual toggle state)
    // Example: if (toggle === 'reporting') filtered = filtered.filter(x => x.opsStatus === 0);
    // Example: if (toggle === 'not-reporting') filtered = filtered.filter(x => x.opsStatus === 1);
    // Example: if (toggle === 'blacklisted') filtered = filtered.filter(x => x.opsStatus === 2);

    return filtered.map((x) => {
      // Color coding for opsStatus
      let color = 'gray';
      if (x.opsStatus === 0)
        color = 'green'; // Installed
      else if (x.opsStatus === 1)
        color = 'red'; // Not Reporting
      else if (x.opsStatus === 2) color = 'black'; // Blacklisted
      // else keep gray
      return {
        ...getCoordinatesPreset('NearReader'),
        latLng: new LatLng(x.lockedLocation!.latitude, x.lockedLocation!.longitude),
        dashArray: [8],
        showMarker: true, // Always show marker
        showCircleMarker: true, // Always show circle
        circleRadius: 280, // Circle radius in meters
        imei: x.imei, // Add IMEI for cross-highlighting
        color,
        fillColor: color,
        opsStatus: x.opsStatus,
        intendedUse: x.intendedUse?.toString(),
        isMobile: x.isMobile,
      };
    });
  }, [readerState.nearReaders]);

  const facilityGeofencesPositions = useMemo<Coordinates[]>((): Coordinates[] => {
    if (!facilitiesState.facilities) return [];

    return facilitiesState.facilities
      .filter((facility) => facility.geofences && facility.geofences?.length > 0)
      .map((fa) => {
        return {
          ...getCoordinatesPreset('Facility'),
          latLng: new LatLng(0, 0), // not used
          geofences: fa.geofences?.map((geof: any) => {
            const geofence = { ...geof };
            // display facilityName from facility.name instead of geofence.facilityName in the map
            geofence.facilityName = fa.name;
            return geofence;
          }),
          showPolygon: true,
        };
      });
  }, [facilitiesState.facilities]);

  const airportGeofencesPositions = useMemo<Coordinates[]>((): Coordinates[] => {
    if (!geofenceState.stationGeofences) return [];

    return geofenceState.stationGeofences.map((geofence) => ({
      ...getCoordinatesPreset('AirportGeofence'),
      latLng: new LatLng(0, 0), // not used
      geofences: [geofence],
      showPolygon: true,
    }));
  }, [geofenceState.stationGeofences]);

  const readerReportsPositions = useMemo<Coordinates[]>((): Coordinates[] => {
    if (!readerReportState.readerReports) return [];

    return readerReportState.readerReports
      .filter((n) => n.location)
      .map((rpt) => ({
        ...getCoordinatesPreset('Calibration'),
        latLng: new LatLng(rpt.location.latitude, rpt.location.longitude),
        markerName: toStandardDate(rpt.lastSeenDateTime),
        showMarker: false,
        showCircleMarker: true,
        circleRadius: 10,
        weight: 1,
        lineCap: 'round',
      }));
  }, [readerReportState.readerReports, toStandardDate]);

  useEffect(() => {
    // Check if all memoized position arrays are populated
    if (
      nearReadersPositions.length > 0 ||
      facilityGeofencesPositions.length > 0 ||
      airportGeofencesPositions.length > 0 ||
      readerReportsPositions.length > 0
    ) {
      setPositions((prevPositions) => {
        let updatedPositions = [...prevPositions];
        let changed = false;

        // Update positions based on nearReadersPositions
        if (!isEqual(prevPositions, nearReadersPositions)) {
          updatedPositions = updatedPositions.filter((v) => v._type !== 'NearReader');
          updatedPositions.push(...nearReadersPositions);
          changed = true;
        }

        // Update positions based on facilityGeofencesPositions
        if (!isEqual(prevPositions, facilityGeofencesPositions)) {
          updatedPositions = updatedPositions.filter((v) => v._type !== 'Facility');
          updatedPositions.push(...facilityGeofencesPositions);
          changed = true;
        }

        // Update positions based on airportGeofencesPositions
        if (!isEqual(prevPositions, airportGeofencesPositions)) {
          updatedPositions = updatedPositions.filter((v) => v._type !== 'AirportGeofence');
          updatedPositions.push(...airportGeofencesPositions);
          changed = true;
        }

        // Update positions based on readerReportsPositions
        if (!isEqual(prevPositions, readerReportsPositions)) {
          updatedPositions = updatedPositions.filter((v) => v._type !== 'Calibration');
          updatedPositions.push(...readerReportsPositions);
          changed = true;
        }

        // Only update the state if there are changes
        if (changed) {
          return updatedPositions;
        } else {
          return prevPositions;
        }
      });
    }
  }, [nearReadersPositions, facilityGeofencesPositions, airportGeofencesPositions, readerReportsPositions]);

  // Function to move the center of the map to a facility
  const handleMoveCenter = useCallback((facility: Facility) => {
    const { location, geofences } = facility;

    // use center from geofence.location if it exists, if not, use center from facility.location
    if (geofences && geofences.length > 0) {
      const geofenceLocation = geofences[0].location;
      if (geofenceLocation) {
        setCenterMap(new LatLng(geofenceLocation.latitude, geofenceLocation.longitude));
      }
      return;
    }
    setCenterMap(new LatLng(location.latitude, location.longitude));
  }, []);

  // Function to handle click events on reader reports
  const onReaderReportClick = (row: { location: { latitude: number; longitude: number } }) => {
    if (row?.location) setCenterMap(new LatLng(row?.location?.latitude, row?.location?.longitude));
  };

  return {
    readerState,
    settingsState,
    facilitiesState,
    centerMap,
    positions,
    shouldFetch,
    handleConfigUpdated,
    handleMoveCenter,
    onReaderReportClick,
    fetchingData,
  };
};

export default useReaderDetail;
